/**
 * BibliotecaExplorer - Sectores, Empresas, Recientes views for the Documentos tab.
 *
 * Sits at the top of the Documentos tab in UnifiedSidebar.
 * Clicking a sector/company applies a filter to the document list below.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, Clock, Layers, ChevronRight, ArrowLeft, FileText } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { apiService } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────
interface Sector {
  name: string;
  doc_count: number;
  company_count: number;
  top_companies: Array<{ name: string; short_name: string; count: number }>;
}

interface Company {
  name: string;
  short_name: string;
  sector: string | null;
  doc_count: number;
}

interface RecentDoc {
  id: string;
  name?: string;
  original_filename?: string;
  company_name?: string;
  short_name?: string;
  document_type?: string;
  document_period?: string;
  shared_category?: string;
  published_at?: string;
  created_at?: string;
}

type ExplorerView = 'tabs' | 'sector-detail' | 'company-detail';
type ExplorerTab = 'sectores' | 'empresas' | 'recientes';

// ─── Props ────────────────────────────────────────────────────────
export interface BibliotecaExplorerProps {
  /** Apply a sector filter to the document list */
  onFilterBySector: (sector: string) => void;
  /** Apply a company filter to the document list */
  onFilterByCompany: (company: string) => void;
  /** Select a document by ID */
  onDocumentSelect?: (documentId: string) => void;
}

// ─── Doc type badge colors (same as AllDocsTab) ───────────────────
const typeBadgeClass = (type: string): string => {
  switch (type) {
    case 'FECU':
      return 'text-blue-400 bg-blue-400/10';
    case 'ANALISIS':
      return 'text-emerald-400 bg-emerald-400/10';
    case 'HE':
      return 'text-amber-400 bg-amber-400/10';
    case 'MEMORIA':
      return 'text-purple-400 bg-purple-400/10';
    case 'XBRL':
      return 'text-cyan-400 bg-cyan-400/10';
    default:
      return 'text-gray-400 bg-gray-400/10';
  }
};

// ─── Component ────────────────────────────────────────────────────
const BibliotecaExplorer: React.FC<BibliotecaExplorerProps> = ({
  onFilterBySector,
  onFilterByCompany,
  onDocumentSelect,
}) => {
  const { theme } = useTheme();

  // View state
  const [activeTab, setActiveTab] = useState<ExplorerTab>('sectores');
  const [view, setView] = useState<ExplorerView>('tabs');
  const [selectedSector, setSelectedSector] = useState<Sector | null>(null);

  // Data
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [recentDocs, setRecentDocs] = useState<RecentDoc[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Search within companies tab
  const [companySearch, setCompanySearch] = useState('');

  // Fetch sectors
  useEffect(() => {
    if (activeTab !== 'sectores' || sectors.length > 0) return;
    setIsLoading(true);
    apiService
      .getSharedSectors()
      .then((res) => {
        if (res.success) setSectors(res.sectors);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [activeTab, sectors.length]);

  // Fetch companies
  useEffect(() => {
    if (activeTab !== 'empresas' || companies.length > 0) return;
    setIsLoading(true);
    apiService
      .getSharedCompaniesList()
      .then((res) => {
        if (res.success) setCompanies(res.companies);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [activeTab, companies.length]);

  // Fetch recent docs
  useEffect(() => {
    if (activeTab !== 'recientes' || recentDocs.length > 0) return;
    setIsLoading(true);
    apiService
      .getSharedRecent(20)
      .then((res) => {
        if (res.success) setRecentDocs(res.documents);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [activeTab, recentDocs.length]);

  const filteredCompanies = useMemo(() => {
    if (!companySearch.trim()) return companies;
    const q = companySearch.toLowerCase();
    return companies.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.short_name.toLowerCase().includes(q) ||
        (c.sector && c.sector.toLowerCase().includes(q))
    );
  }, [companies, companySearch]);

  const handleSectorClick = useCallback((sector: Sector) => {
    setSelectedSector(sector);
    setView('sector-detail');
  }, []);

  const handleSectorFilter = useCallback(
    (sectorName: string) => {
      onFilterBySector(sectorName);
    },
    [onFilterBySector]
  );

  const handleCompanyFilter = useCallback(
    (companyName: string) => {
      onFilterByCompany(companyName);
    },
    [onFilterByCompany]
  );

  const handleBack = useCallback(() => {
    setView('tabs');
    setSelectedSector(null);
  }, []);

  // ─── Tab buttons ────────────────────────────────────────────────
  const tabs: { key: ExplorerTab; label: string; icon: typeof Layers }[] = [
    { key: 'sectores', label: 'Sectores', icon: Layers },
    { key: 'empresas', label: 'Empresas', icon: Building2 },
    { key: 'recientes', label: 'Recientes', icon: Clock },
  ];

  // ─── Render sector detail ──────────────────────────────────────
  if (view === 'sector-detail' && selectedSector) {
    return (
      <div className="flex flex-col">
        {/* Back header */}
        <button
          onClick={handleBack}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs ${theme.colors.text.secondary} hover:${theme.colors.text.primary} transition-colors`}
        >
          <ArrowLeft className="w-3 h-3" />
          Sectores
        </button>
        {/* Sector header card */}
        <div className={`px-3 py-2 border-b ${theme.colors.border.primary}`}>
          <div className={`text-sm font-medium ${theme.colors.text.primary}`}>
            {selectedSector.name}
          </div>
          <div className={`text-xs ${theme.colors.text.secondary} mt-0.5`}>
            {selectedSector.doc_count} documentos · {selectedSector.company_count} empresas
          </div>
        </div>
        {/* Filter action */}
        <button
          onClick={() => handleSectorFilter(selectedSector.name)}
          className={`mx-3 mt-2 mb-1 px-3 py-1.5 text-xs rounded-lg bg-nb-accent/10 text-nb-accent hover:bg-nb-accent/20 transition-colors min-h-[44px]`}
        >
          Ver todos los documentos de {selectedSector.name}
        </button>
        {/* Top companies */}
        <div className={`px-3 py-1.5 text-xs font-medium ${theme.colors.text.muted}`}>
          Principales empresas
        </div>
        {selectedSector.top_companies.map((company) => (
          <button
            key={company.name}
            onClick={() => handleCompanyFilter(company.name)}
            className={`w-full flex items-center justify-between px-3 py-1.5 text-xs hover:${theme.colors.bg.secondary} transition-colors min-h-[44px]`}
          >
            <span className={`${theme.colors.text.primary} truncate`}>{company.short_name}</span>
            <span className={`${theme.colors.text.muted} flex-shrink-0 ml-2`}>{company.count}</span>
          </button>
        ))}
      </div>
    );
  }

  // ─── Render main tabs view ─────────────────────────────────────
  return (
    <div className="flex flex-col">
      {/* Tab bar */}
      <div
        className={`flex items-center gap-0.5 px-3 py-1 border-b ${theme.colors.border.primary}`}
      >
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => {
              setActiveTab(key);
              setView('tabs');
            }}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors min-h-[36px] ${
              activeTab === key
                ? `font-medium ${theme.colors.text.primary} ${theme.colors.bg.secondary}`
                : `${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary}`
            }`}
          >
            <Icon className="w-3 h-3" />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className={`px-4 py-6 text-center ${theme.colors.text.secondary}`}>
          <div className="animate-spin w-4 h-4 border-2 border-nb-accent border-t-transparent rounded-full mx-auto mb-2" />
          <div className="text-xs">Cargando...</div>
        </div>
      ) : (
        <>
          {/* ═══ Sectores ═══════════════════════════════════════ */}
          {activeTab === 'sectores' && (
            <div className="py-1">
              {sectors.map((sector) => (
                <button
                  key={sector.name}
                  onClick={() => handleSectorClick(sector)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:${theme.colors.bg.secondary} transition-colors group min-h-[44px]`}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-medium ${theme.colors.text.primary}`}>
                      {sector.name}
                    </div>
                    <div className={`text-[10px] ${theme.colors.text.muted} mt-0.5`}>
                      {sector.doc_count} docs · {sector.company_count} empresas
                    </div>
                  </div>
                  <ChevronRight
                    className={`w-3 h-3 ${theme.colors.text.muted} opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0`}
                  />
                </button>
              ))}
            </div>
          )}

          {/* ═══ Empresas ═══════════════════════════════════════ */}
          {activeTab === 'empresas' && (
            <div className="flex flex-col">
              {/* Company search */}
              <div className="px-3 py-1.5">
                <input
                  type="text"
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  placeholder="Buscar empresa..."
                  className={`w-full px-2 py-1 text-xs rounded bg-transparent hover:${theme.colors.bg.input} focus:${theme.colors.bg.input} ${theme.colors.text.primary} focus:outline-none focus:ring-1 focus:ring-nb-accent/40 transition-colors`}
                />
              </div>
              <div className="py-1 max-h-[300px] overflow-y-auto scrollbar-thin">
                {filteredCompanies.slice(0, 100).map((company) => (
                  <button
                    key={company.name}
                    onClick={() => handleCompanyFilter(company.name)}
                    className={`w-full flex items-center gap-2 px-3 py-1 text-left hover:${theme.colors.bg.secondary} transition-colors min-h-[36px]`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs ${theme.colors.text.primary} truncate`}>
                        {company.short_name}
                      </div>
                      {company.sector && (
                        <div className={`text-[10px] ${theme.colors.text.muted} truncate`}>
                          {company.sector}
                        </div>
                      )}
                    </div>
                    <span className={`text-[10px] ${theme.colors.text.muted} flex-shrink-0`}>
                      {company.doc_count}
                    </span>
                  </button>
                ))}
                {filteredCompanies.length === 0 && (
                  <div className={`px-3 py-4 text-center text-xs ${theme.colors.text.muted}`}>
                    Sin resultados
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══ Recientes ═════════════════════════════════════ */}
          {activeTab === 'recientes' && (
            <div className="py-1">
              {recentDocs.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => onDocumentSelect?.(doc.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:${theme.colors.bg.secondary} transition-colors min-h-[44px]`}
                >
                  <FileText className={`w-3.5 h-3.5 flex-shrink-0 ${theme.colors.text.muted}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-medium ${theme.colors.text.primary} truncate`}>
                      {doc.short_name ||
                        doc.company_name ||
                        doc.name ||
                        doc.original_filename ||
                        'Documento'}
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      {doc.document_type && (
                        <span
                          className={`text-[10px] font-medium px-1 rounded-lg ${typeBadgeClass(
                            doc.document_type
                          )}`}
                        >
                          {doc.document_type}
                        </span>
                      )}
                      {doc.shared_category && (
                        <>
                          {doc.document_type && (
                            <span className={`text-[10px] ${theme.colors.text.muted}`}>·</span>
                          )}
                          <span className={`text-[10px] ${theme.colors.text.muted}`}>
                            {doc.shared_category}
                          </span>
                        </>
                      )}
                      {doc.document_period && (
                        <>
                          <span className={`text-[10px] ${theme.colors.text.muted}`}>·</span>
                          <span className={`text-[10px] ${theme.colors.text.secondary}`}>
                            {doc.document_period}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              ))}
              {recentDocs.length === 0 && (
                <div className={`px-4 py-6 text-center text-xs ${theme.colors.text.muted}`}>
                  No hay documentos recientes
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(BibliotecaExplorer);
